/**
 * Partner-engagement derivations (build-specs/partner-engagement-analytics.md).
 *
 * PURE, display-only helpers behind the "Partner engagement" block on the
 * Reports → Referrals page. They JOIN two things on the wire — no new route:
 *   - operating weekly rows (`sections.partners`): per week, per partner, the
 *     interactions logged (`touches` — THE CORE METRIC) and an optional pie
 *     date. Touches are real WEEKLY data.
 *   - the native referral report (`/reports/referrals/weekly`): per-partner
 *     rollups PLUS the per-partner weekly series (`weekly_new_referrals`,
 *     oldest-first, index-aligned to `buckets` — the additive field this
 *     build exposed from the grain referralReports.ts already computed) and
 *     the aggregate weekly buckets.
 *
 * PIE-LIFT WINDOW CONVENTION (symmetric by construction):
 *   before = the up-to-13 weekly buckets STRICTLY BEFORE the pie week;
 *   after  = the up-to-13 weekly buckets STRICTLY AFTER the pie week;
 *   the pie week itself is EXCLUDED from BOTH sides — it is the delivery
 *   week, causally ambiguous. To avoid truncated-window bias the two sides
 *   are compared over EQUAL observed-week counts: observed = min(available
 *   before, available after, 13); a recent pie with zero observed after-weeks
 *   renders "too early", never a Δ from unequal windows. Lift is computed on
 *   the PARTNER'S OWN weekly referral series — directional, small n, NOT
 *   attribution (the printed caveat says so).
 *
 * The join is by NAME: an operating partner `name` is matched to a referral
 * `source.label` by normalization (NFC, casefold, trim, collapse internal
 * whitespace) plus an explicit alias map from tenant config
 * (`config.reports.partner_aliases`, { attio_name: directory_name }) for the
 * misses. Names are opaque strings (partner names, never client PHI).
 *
 * SILENT-MERGE RISK (accepted by design, FE-only join): two distinct partners
 * whose names normalize to the same key (e.g. "ACME Care" vs "Acme  care"),
 * or an alias-map typo pointing one partner at another's Directory name, will
 * silently MERGE into one engagement row (touches summed, pies pooled). There
 * is no FK to disambiguate — names are deliberately opaque strings. The
 * mitigation is operational: keep Attio display names distinct and review the
 * alias map when adding entries.
 */

import type {
  WeeklyOperatingMetricWire,
  OperatingPartnerTouch,
  ReferralPartnerRowWire,
  ReferralWeekWire,
} from '@/lib/api';

// ---------------------------------------------------------------------
// Name normalization + alias join
// ---------------------------------------------------------------------

/** NFC-normalize, casefold, trim, collapse internal whitespace — the join
 *  key normalizer. MUST stay in sync with the validator's canonical-sort key
 *  (api/_shared/validators/operatingMetrics.ts normalizedPartnerKey). */
export function normalizePartnerName(name: string): string {
  return name.normalize('NFC').trim().toLowerCase().replace(/\s+/g, ' ');
}

/**
 * Read the tenant partner-alias map from `config.reports.partner_aliases`
 * ({ attio_name: directory_name }), falling back to `{}` when unset or
 * malformed (ADR-0009 config-seam posture; the readRateBands defensive-walk
 * precedent — only well-formed string→string entries survive). Keyed by the
 * Attio display name; the value is the Directory source name it should join
 * to. Pure.
 */
export function readPartnerAliases(config: unknown): Record<string, string> {
  if (typeof config !== 'object' || config === null) return {};
  const reports = (config as Record<string, unknown>).reports;
  if (typeof reports !== 'object' || reports === null) return {};
  const aliases = (reports as Record<string, unknown>).partner_aliases;
  if (typeof aliases !== 'object' || aliases === null || Array.isArray(aliases)) return {};
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(aliases as Record<string, unknown>)) {
    if (typeof k === 'string' && k.trim().length > 0 && typeof v === 'string' && v.trim().length > 0) {
      out[k] = v;
    }
  }
  return out;
}

/**
 * The normalized join key for a partner name: apply the alias map (matched on
 * the raw name AND its normalized form, so a config author need not pre-
 * normalize), THEN normalize the result. Pure. See the module-header
 * SILENT-MERGE RISK note: colliding normalized keys or an alias typo merge
 * two partners into one row — unmitigated by design in this FE-only join.
 */
export function partnerJoinKey(rawName: string, aliases: Record<string, string>): string {
  const direct = aliases[rawName] ?? aliases[rawName.trim()] ?? aliases[normalizePartnerName(rawName)];
  return normalizePartnerName(direct ?? rawName);
}

// ---------------------------------------------------------------------
// Per-partner engagement rows
// ---------------------------------------------------------------------

export interface PartnerEngagementRow {
  /** Display name (the operating/Attio name as sent). */
  name: string;
  tier: string | null;
  /** Weekly touches, oldest-first, aligned to `weekEndings` (0 where the
   *  partner was absent that week — absence reads as no activity). Duplicate
   *  same-name entries within one week are SUMMED (payload-v2 contract). */
  touchSeries: number[];
  /** Aligned to `weekEndings`: true where a pie was delivered that week. */
  pieWeeks: boolean[];
  /** The raw pie dates (for the lift table). */
  pieDates: string[];
  totalTouches: number;
  /** Matched referral rollup (null when the name did not join). */
  referralWindowTotal: number | null;
  referralRecentAvg: number | null;
  conversionRate: number | null;
  /** THIS partner's weekly new referrals, oldest-first, index-aligned to the
   *  REFERRAL report's `buckets` (null when unmatched or the server predates
   *  the additive field). Feeds the per-partner referral sparkline + lift. */
  referralWeekly: number[] | null;
  /** THE actionable alarm: touches = 0 for the latest 3+ consecutive weeks
   *  AND a positive trailing referral average — a partner still sending us
   *  referrals whom we have stopped touching. */
  quiet: boolean;
}

/** The minimum trailing all-zero-touch weeks that trip the quiet flag. */
export const QUIET_MIN_WEEKS = 3;

/** Count of trailing consecutive zero-touch weeks at the end of a series. */
export function trailingZeroTouchWeeks(series: number[]): number {
  let n = 0;
  for (let i = series.length - 1; i >= 0; i--) {
    if (series[i] === 0) n++;
    else break;
  }
  return n;
}

/**
 * Join operating `sections.partners` (weekly touches + pies) to the referral
 * report by normalized name + alias map. `operatingRows` is newest-first (the
 * wire order); the returned series are oldest-first. Partners are ordered by
 * total touches desc, then name asc. Pure.
 */
export function buildPartnerEngagement(
  operatingRows: WeeklyOperatingMetricWire[],
  referralPartners: ReferralPartnerRowWire[],
  aliases: Record<string, string>,
): { rows: PartnerEngagementRow[]; weekEndings: string[] } {
  // Oldest-first, aligned week axis.
  const chrono = [...operatingRows].reverse();
  const weekEndings = chrono.map((r) => r.week_ending);
  const nWeeks = weekEndings.length;

  // Referral rollup lookup by normalized source label.
  const referralByKey = new Map<string, ReferralPartnerRowWire>();
  for (const p of referralPartners) {
    referralByKey.set(normalizePartnerName(p.source.label), p);
  }

  // Accumulate each partner's weekly touches + pie weeks across the window.
  interface Acc {
    name: string;
    tier: string | null;
    touchSeries: number[];
    pieWeeks: boolean[];
    pieDates: string[];
    totalTouches: number;
  }
  const byPartner = new Map<string, Acc>();

  chrono.forEach((row, wi) => {
    const partners: OperatingPartnerTouch[] = row.sections?.partners ?? [];
    for (const p of partners) {
      const dedupeKey = partnerJoinKey(p.name, aliases);
      let acc = byPartner.get(dedupeKey);
      if (!acc) {
        acc = {
          name: p.name,
          tier: p.tier ?? null,
          touchSeries: new Array<number>(nWeeks).fill(0),
          pieWeeks: new Array<boolean>(nWeeks).fill(false),
          pieDates: [],
          totalTouches: 0,
        };
        byPartner.set(dedupeKey, acc);
      }
      // Latest non-null tier wins (chrono → the most recent week seen last).
      if (p.tier != null) acc.tier = p.tier;
      // += also SUMS duplicate same-name entries within one week (contract).
      acc.touchSeries[wi] += p.touches;
      acc.totalTouches += p.touches;
      if (p.pie) {
        acc.pieWeeks[wi] = true;
        acc.pieDates.push(p.pie);
      }
    }
  });

  const rows: PartnerEngagementRow[] = [];
  for (const [key, acc] of byPartner.entries()) {
    const matched = referralByKey.get(key);
    const recentAvg = matched ? matched.recent_avg : null;
    const quiet =
      trailingZeroTouchWeeks(acc.touchSeries) >= QUIET_MIN_WEEKS && recentAvg !== null && recentAvg > 0;
    rows.push({
      name: acc.name,
      tier: acc.tier,
      touchSeries: acc.touchSeries,
      pieWeeks: acc.pieWeeks,
      pieDates: acc.pieDates.slice().sort(),
      totalTouches: acc.totalTouches,
      referralWindowTotal: matched ? matched.window_total : null,
      referralRecentAvg: recentAvg,
      conversionRate: matched ? matched.conversion_rate : null,
      referralWeekly: matched?.weekly_new_referrals ?? null,
      quiet,
    });
  }
  rows.sort((a, b) => b.totalTouches - a.totalTouches || a.name.localeCompare(b.name));
  return { rows, weekEndings };
}

// ---------------------------------------------------------------------
// Aggregate weekly series (block-header sparklines)
// ---------------------------------------------------------------------

/**
 * Total touches per week across ALL partners, oldest-first, aligned to the
 * operating rows. Pure.
 */
export function aggregateTouchesByWeek(operatingRows: WeeklyOperatingMetricWire[]): number[] {
  return [...operatingRows].reverse().map((r) => (r.sections?.partners ?? []).reduce((s, p) => s + p.touches, 0));
}

// ---------------------------------------------------------------------
// Pie lift — per-partner, symmetric windows (see the module header for the
// convention: pie week excluded from both sides; equal observed-week counts).
// ---------------------------------------------------------------------

/** ~90 days ≈ 13 weekly buckets each side. */
export const LIFT_BUCKETS = 13;

export interface PieLiftRow {
  partner: string;
  pie: string; // pie-delivered date (YYYY-MM-DD)
  /** THIS partner's referrals over the `observedWeeks` weeks strictly before
   *  the pie week. null when not comparable (no join / too early / no
   *  baseline). */
  before: number | null;
  /** THIS partner's referrals over the `observedWeeks` weeks strictly after
   *  the pie week. null when not comparable. */
  after: number | null;
  /** Weeks counted on EACH side (equal by construction; <= 13). */
  observedWeeks: number;
  /** True when zero after-weeks exist yet (a recent pie) — render "too
   *  early", never a Δ. */
  tooEarly: boolean;
  /** True when the partner's name did not join to a referral source (no
   *  per-partner series to measure against). */
  noJoin: boolean;
}

export interface PieLift {
  rows: PieLiftRow[];
  /** Board topline: total window referrals / total pies (null when no pies). */
  topline: { pies: number; referrals: number; ratio: number | null };
}

/** Index of the referral bucket whose Sunday-ending week contains `date`, or
 *  the nearest bucket by week_end when the date falls between windows. -1 when
 *  no buckets. */
export function bucketIndexForDate(date: string, buckets: ReferralWeekWire[]): number {
  if (buckets.length === 0) return -1;
  // A bucket covers [week_start, week_end] inclusive (both date-only UTC).
  for (let i = 0; i < buckets.length; i++) {
    if (date >= buckets[i]!.week_start && date <= buckets[i]!.week_end) return i;
  }
  // Outside every window: clamp to the nearest edge by week_end comparison.
  if (date < buckets[0]!.week_start) return 0;
  return buckets.length - 1;
}

/**
 * Per-pie lift measured on the PARTNER'S OWN weekly referral series
 * (`weekly_new_referrals`, index-aligned to `referralBuckets`), symmetric
 * windows per the module-header convention:
 *   observed = min(weeks available before, weeks available after, 13);
 *   before   = sum over [pi - observed, pi)   — pie week EXCLUDED;
 *   after    = sum over (pi, pi + observed]   — pie week EXCLUDED;
 * zero after-weeks (a recent pie) → tooEarly, no Δ. Rows are newest pie
 * first. The topline stays AGGREGATE (total window referrals / pies) — the
 * board line. Pure.
 */
export function buildPieLift(
  rows: PartnerEngagementRow[],
  referralBuckets: ReferralWeekWire[],
): PieLift {
  const liftRows: PieLiftRow[] = [];
  let pies = 0;

  for (const r of rows) {
    for (const pie of r.pieDates) {
      pies++;
      const series = r.referralWeekly;
      const pi = bucketIndexForDate(pie, referralBuckets);
      if (series === null || pi < 0) {
        liftRows.push({ partner: r.name, pie, before: null, after: null, observedWeeks: 0, tooEarly: false, noJoin: true });
        continue;
      }
      const beforeAvail = Math.min(pi, LIFT_BUCKETS);
      const afterAvail = Math.min(Math.max(0, series.length - pi - 1), LIFT_BUCKETS);
      const observedWeeks = Math.min(beforeAvail, afterAvail);
      if (observedWeeks === 0) {
        // Symmetry has nothing to compare: a recent pie (no after-weeks yet)
        // is "too early"; a pie at the window's very start has no baseline.
        liftRows.push({
          partner: r.name,
          pie,
          before: null,
          after: null,
          observedWeeks: 0,
          tooEarly: afterAvail === 0,
          noJoin: false,
        });
        continue;
      }
      const before = series.slice(pi - observedWeeks, pi).reduce((s, n) => s + n, 0);
      const after = series.slice(pi + 1, pi + 1 + observedWeeks).reduce((s, n) => s + n, 0);
      liftRows.push({ partner: r.name, pie, before, after, observedWeeks, tooEarly: false, noJoin: false });
    }
  }

  liftRows.sort((a, b) => (a.pie < b.pie ? 1 : a.pie > b.pie ? -1 : a.partner.localeCompare(b.partner)));

  const referrals = referralBuckets.reduce((s, b) => s + b.new_referrals, 0);
  return {
    rows: liftRows,
    topline: { pies, referrals, ratio: pies === 0 ? null : referrals / pies },
  };
}
